import { Button } from 'components/atoms';
import { FormInput } from 'components/molecules';
import { PublicPage } from 'components/templates';
import { passwordRegExp } from 'constants/passwordRegExp';
import { Form, Formik, FormikHelpers } from 'formik';
import { useMutateOptions, useResetPasswordNavigation } from 'hooks';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { PiCaretLeft, PiLock } from 'react-icons/pi';
import { useResetPassword } from 'services/auth/resetPassword';
import { IResetPasswordForm } from 'services/auth/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { object, ref, string } from 'yup';

const resetPasswordValidationSchema = () =>
  object().shape({
    newPassword: string()
      .matches(passwordRegExp, i18next.t('formik-error.invalid-password'))
      .required(i18next.t('formik-error.required')),
    newPasswordConfirm: string()
      .oneOf(
        [ref('newPassword'), ''],
        i18next.t('formik-error.unmatch-password')
      )
      .required(i18next.t('formik-error.required')),
  });

const ResetPassword = () => {
  const { t } = useTranslation();
  const { goToConfirmation, goToLogin, email, token } =
    useResetPasswordNavigation();

  const resetPassword = useResetPassword();

  const mutateOptions = useMutateOptions<IResetPasswordForm>();

  const onResetPassword = async (
    values: IResetPasswordForm,
    helpers: FormikHelpers<IResetPasswordForm>
  ) => {
    const { newPasswordConfirm, ...resetPasswordMutation } = values;
    await resetPassword.mutateAsync(resetPasswordMutation, {
      ...mutateOptions(helpers),
      onSuccess: () => goToConfirmation(),
    });
  };

  return (
    <PublicPage
      header={
        <>
          <PiLock />
          {t('reset-password.header')}
        </>
      }
      footer={
        <div
          className='flex gap-2 cursor-pointer p-4 items-center'
          onClick={() => goToLogin()}>
          <PiCaretLeft />
          {t('reset-password.back')}
        </div>
      }>
      <div className=' w-72 opacity-65 text-center'>
        {t('reset-password.password-reg-exp')}
      </div>
      <Formik
        onSubmit={onResetPassword}
        initialValues={{
          email: email,
          token: token,
          newPassword: '',
          newPasswordConfirm: '',
        }}
        enableReinitialize
        validateOnBlur
        validationSchema={resetPasswordValidationSchema}>
        <Form>
          <FormInput.Text
            name='newPassword'
            type='password'
            label={t('input.label.password')}
            borderless
            appBackground
          />
          <FormInput.Text
            name='newPasswordConfirm'
            type='password'
            label={t('input.label.confirm-password')}
            borderless
            appBackground
          />
          <Button
            type='submit'
            variant={BUTTON_VARIANT.submit}
            isLoading={resetPassword.isPending}
            fullWidth>
            <Button.Text text={t('reset-password.button')} />
          </Button>
        </Form>
      </Formik>
    </PublicPage>
  );
};

export default ResetPassword;
